import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import api from '../../services/api';

// Boletos que o financeiro importou/lançou no ERP para o cliente (leitura ao vivo, com o PDF anexado).
// `baseUrl`: "/clientes/<id>" (ficha do cliente) ou "contratos/<id>" (Vendas › Boletos).

const fmtBRL = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const fmtData = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};
const STATUS = {
  PENDENTE: ['Em aberto', 'bg-yellow-100 text-yellow-800'],
  PAGO: ['Pago', 'bg-green-100 text-green-800'],
  VENCIDO: ['Vencido', 'bg-red-100 text-red-700'],
  CANCELADA: ['Cancelada', 'bg-gray-100 text-gray-500'],
  CANCELADO: ['Cancelada', 'bg-gray-100 text-gray-500'],
};

async function abrirBoleto(baseUrl, cobrancaId, nome) {
  // abre a aba já no clique (senão o bloqueador de pop-up barra depois do await)
  const aba = window.open('', '_blank');
  try {
    const res = await api.get(`${baseUrl}/erp/cobrancas/${cobrancaId}/boleto`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    if (aba) aba.location.href = url;
    else { const a = document.createElement('a'); a.href = url; a.download = nome || 'boleto.pdf'; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (aba) aba.close();
    let msg = err.message;
    try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch { /* blob não-JSON */ }
    alert('Erro ao abrir o boleto: ' + msg);
  }
}

export default function BoletosErpBox({ baseUrl, compacto = false }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['erp-cobrancas', baseUrl],
    queryFn: () => api.get(`${baseUrl}/erp/cobrancas`).then((r) => r.data.data),
  });

  if (isLoading) return <div className="text-sm text-gray-500">Carregando…</div>;
  if (error) return <div className="text-sm text-red-600">Erro: {error.response?.data?.error || error.message}</div>;
  if (!data?.clienteEncontrado) return <div className="text-sm text-gray-400">Cliente ainda não sincronizado no ERP.</div>;
  if (!data.cobrancas?.length) return <div className="text-sm text-gray-400">Nenhum boleto lançado pelo financeiro ainda.</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1 pr-3">Descrição</th>
            {!compacto && <th className="py-1 pr-3">Unidade</th>}
            <th className="py-1 pr-3">Vencimento</th>
            <th className="py-1 pr-3">Valor</th>
            <th className="py-1 pr-3">Status</th>
            <th className="py-1">Boleto</th>
          </tr>
        </thead>
        <tbody>
          {data.cobrancas.map((c) => {
            const [rotulo, cor] = STATUS[c.status] || [c.status, 'bg-gray-100 text-gray-600'];
            return (
              <tr key={c.id} className="border-t">
                <td className="py-1.5 pr-3">{c.descricao}</td>
                {!compacto && <td className="py-1.5 pr-3">{c.unidade || '—'}</td>}
                <td className="py-1.5 pr-3 whitespace-nowrap">{fmtData(c.vencimento)}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap">{fmtBRL(c.valor)}</td>
                <td className="py-1.5 pr-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cor}`}>{rotulo}</span>
                  {c.pagoEm && <div className="text-xs text-gray-400 mt-0.5">em {fmtData(c.pagoEm)}</div>}
                </td>
                <td className="py-1.5">
                  {c.temBoleto ? (
                    <button onClick={() => abrirBoleto(baseUrl, c.id, c.boletoNome)} className="inline-flex items-center gap-1 text-blue-600 hover:underline" title="Abrir o boleto em PDF">
                      <FileText size={14} /> PDF
                    </button>
                  ) : c.asaasUrl ? (
                    <a href={c.asaasUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">abrir</a>
                  ) : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
